import { ApolloClient } from "@apollo/client";
import type {
  GetOrganizationsDTO,
  UpdateOrganizationDTO,
} from "@src/domain/dto/organizations";
import {
  Organization,
  type IOrganizationProps,
} from "@src/domain/entities/organization";
import type {
  IOrganizationGetOrganizationsResult,
  IOrganizationGetDetailsResult,
  IOrganizationPaginatedResponse,
  IOrganizationDetailsResponse,
  IOrganizationRepository,
} from "@src/domain/repositories/organization";
import {
  GET_ORGANIZATIONS,
  GET_ORGANIZATION_DETAILS,
} from "@src/domain/graphql";
import { AdminRestApiPaths } from "@src/infrastructure/adapters/http";
import { MultipartFormService } from "@src/infrastructure/services/multipartform";

export class OrganizationRepository implements IOrganizationRepository {
  private readonly client: ApolloClient;

  constructor(client: ApolloClient) {
    this.client = client;
  }

  invalidateOrganizationsCache(): void {
    this.client.cache.evict({ id: "ROOT_QUERY", fieldName: "organizations" });
    this.client.cache.evict({
      id: "ROOT_QUERY",
      fieldName: "organizationDetails",
    });
  }

  async getOrganizations(
    params: GetOrganizationsDTO,
    refetch?: boolean,
  ): Promise<IOrganizationGetOrganizationsResult> {
    if (refetch) {
      this.invalidateOrganizationsCache();
    }
    const response = await this.fetchOrganizationsFromApi(params);
    return { data: response };
  }

  private async fetchOrganizationsFromApi(
    params: GetOrganizationsDTO,
  ): Promise<IOrganizationPaginatedResponse> {
    const result = await this.client.query<IOrganizationPaginatedResponse>({
      query: GET_ORGANIZATIONS,
      variables: {
        params: {
          page_number: params.page_number,
          page_size: params.page_size,
          organization_ids: params.organization_ids,
        },
      },
    });
    if (!result.data) throw new Error("No organizations found");
    return result.data;
  }

  async getOrganizationDetails(
    organizationId: number,
  ): Promise<IOrganizationGetDetailsResult> {
    const result = await this.client.query<IOrganizationDetailsResponse>({
      query: GET_ORGANIZATION_DETAILS,
      variables: { organizationId },
    });
    if (!result.data) throw new Error("Organization details not found");
    return { data: result.data };
  }

  async updateOrganization(
    params: UpdateOrganizationDTO,
  ): Promise<Organization> {
    const { data } = await new MultipartFormService().post<{
      data: IOrganizationProps;
    }>(AdminRestApiPaths.organization, params.toMultipartFormData());

    if (!data?.organizationId) {
      throw new Error("Organization save failed");
    }

    this.invalidateOrganizationsCache();
    return new Organization({
      ...data,
      brands: data.brands ?? [],
    });
  }
}
